import { CameraOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Form, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { cameraService } from "../services/camera.service";
import { nvrService } from "@/modules/nvr/services/nvr.service";

const { Title } = Typography;

export function CamerasPage() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [form] = Form.useForm();

	const { data, isLoading } = useQuery({
		queryKey: ["cameras"],
		queryFn: () => cameraService.list({ per_page: 100 }),
	});

	const { data: nvrsData } = useQuery({
		queryKey: ["nvrs-select"],
		queryFn: () => nvrService.list({ per_page: 100 }),
	});

	const saveMutation = useMutation({
		mutationFn: (v: Record<string, unknown>) => editingId ? cameraService.update(editingId, v) : cameraService.create(v),
		onSuccess: () => {
			message.success(editingId ? "Yangilandi" : "Qo'shildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["cameras"] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => cameraService.remove(id),
		onSuccess: () => { message.success("O'chirildi"); queryClient.invalidateQueries({ queryKey: ["cameras"] }); },
	});

	const captureMutation = useMutation({
		mutationFn: (id: number) => cameraService.capture(id),
		onSuccess: () => message.success("Screenshot so'rov yuborildi"),
	});

	const openCreate = () => { setEditingId(null); form.resetFields(); setDrawerOpen(true); };
	const openEdit = (record: Record<string, unknown>) => { setEditingId(record.id as number); form.setFieldsValue(record); setDrawerOpen(true); };
	const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); form.resetFields(); };

	const cameras = data?.data?.cameras || [];
	const typeColor: Record<string, string> = { hall: "blue", stage: "purple" };

	const columns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{ title: "Nomi", dataIndex: "name" },
		{ title: "NVR", key: "nvr", render: (_: unknown, r: Record<string, unknown>) => (r.nvr as { name: string } | null)?.name || r.nvr_name || "—" },
		{ title: "Kanal", dataIndex: "channel" },
		{ title: "Serial", dataIndex: "serial" },
		{ title: "Turi", dataIndex: "camera_type", render: (v: string) => <Tag color={typeColor[v]}>{v === "hall" ? "Zal" : "Sahna"}</Tag> },
		{
			title: "Amallar", width: 150,
			render: (_: unknown, r: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<CameraOutlined />} onClick={() => captureMutation.mutate(r.id as number)} title="Screenshot" />
					<Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
					<Popconfirm title="O'chirish?" onConfirm={() => deleteMutation.mutate(r.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">Kameralar</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Qo'shish</Button>
			</div>
			<Table columns={columns} dataSource={cameras} rowKey="id" loading={isLoading} pagination={{ pageSize: 20, showSizeChanger: true }} />

			<Drawer title={editingId ? "Kamera tahrirlash" : "Yangi kamera"} open={drawerOpen} onClose={closeDrawer} width={480}
				extra={<Space><Button onClick={closeDrawer}>Bekor qilish</Button><Button type="primary" onClick={() => form.submit()} loading={saveMutation.isPending}>Saqlash</Button></Space>}>
				<Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
					<Form.Item name="nvr_id" label="NVR" rules={[{ required: true }]}>
						<Select placeholder="Tanlang" showSearch optionFilterProp="label"
							options={nvrsData?.data?.nvrs?.map((n: { id: number; name: string }) => ({ value: n.id, label: n.name }))} />
					</Form.Item>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}><Input /></Form.Item>
					<Form.Item name="channel" label="Kanal raqami"><InputNumber className="w-full" /></Form.Item>
					<Form.Item name="camera_type" label="Turi" initialValue="hall">
						<Select options={[{ value: "hall", label: "Zal (odam sanash)" }, { value: "stage", label: "Sahna (sanatkor aniqlash)" }]} />
					</Form.Item>
					<Form.Item name="serial" label="Serial raqam"><Input /></Form.Item>
					<Form.Item name="ip" label="IP manzil"><Input /></Form.Item>
					<Form.Item name="resolution" label="Sifat"><Input placeholder="1080p" /></Form.Item>
					<Form.Item name="location" label="Joylashuv"><Input placeholder="Asosiy zal" /></Form.Item>
				</Form>
			</Drawer>
		</div>
	);
}
